import { drfRequest } from '@/services/drfRequest'

const BASE = '/api/v1/crm-campaigns'

// Los campos van en castellano porque son los del modelo (`CrmCampaign`) y los
// del contrato de la API: cambiarles el nombre aquí obligaría a traducir en cada
// petición.

export type CampaignStatus = 'borrador' | 'activa' | 'pausada' | 'terminada' | 'archivada'
export type AudienceType = 'filtros' | 'csv'
export type ConsentCheckStatus = 'pendiente' | 'en_curso' | 'hecho'
export type RecipientStatus =
  | 'pendiente'
  | 'en_curso'
  | 'respondido'
  | 'baja'
  | 'parado_gestion_agente'
  | 'parado_demanda_baja'
  | 'descartado_rgpd'
  | 'completado'
  | 'fallido'
  | 'excluido'

export interface CampaignZone {
  id: number
  nombre: string
}

/** `CrmCampaign.filtros`, tal cual lo guarda el servidor. Todo es opcional. */
export interface CampaignFilters {
  estados?: string[]
  dias_sin_gestion_min?: number | null
  dias_sin_gestion_max?: number | null
  provincias?: string[]
  municipios?: string[]
  poblaciones?: string[]
  zonas?: CampaignZone[]
  precio_hasta_min?: number | null
  precio_hasta_max?: number | null
  gestores?: string[]
  tipos_operacion?: string[]
  tipos_inmueble?: string[]
  habitaciones_min?: number | null
  extras?: string[]
}

/** El esquema de `campaigns/template_variables`. Una constante lleva su texto en `source_key`. */
export interface VariableMappingEntry {
  position: number
  source_type: 'crm_demand' | 'constant'
  source_key: string
  fallback_text?: string
}

export interface CampaignStep {
  orden: number
  plantilla_nombre: string
  plantilla_idioma: string
  mapeo_variables: VariableMappingEntry[]
  dia_offset: number
  hora_envio: string
  enlace_buscador: boolean
  enlace_fijo: string
}

export interface CrmCampaign {
  id: string
  nombre: string
  estado: CampaignStatus
  audiencia_tipo: AudienceType
  filtros: CampaignFilters
  excluir_agentes_excluidos: boolean
  parar_si_responde: boolean
  consentimiento_estado: ConsentCheckStatus
  consentimiento_comprobados: number
  inicio_at: string | null
  lanzada_at: string | null
  creada_por: string
  created_at: string
  updated_at: string
  steps: CampaignStep[]
  destinatarios: number
  enviados: number
  respondidos: number
}

export type NewCampaign = Pick<
  CrmCampaign,
  'nombre' | 'audiencia_tipo' | 'excluir_agentes_excluidos' | 'parar_si_responde'
> & { filtros?: CampaignFilters }

export interface AudiencePreview {
  total: number
  incluidos: number
  excluidos_por_motivo: Record<string, number>
  sin_zona_dibujada: number
}

export interface AudienceConsent {
  estado: ConsentCheckStatus
  comprobados: number
  /** Los incluidos antes de descartar: es la base de la barra y no cambia mientras se comprueba. */
  total: number
  descartados_por_motivo: Record<string, number>
}

export interface CsvReport {
  validas: number
  invalidas: Array<{ fila: number; valor: string; motivo: string }>
  duplicadas: number
}

export interface CampaignRecipient {
  id: string
  telefono_e164: string
  nombre: string
  id_demanda: string
  id_contacto: string
  id_gestor: string
  origen: 'crm' | 'csv'
  incluido: boolean
  motivo_exclusion: string
  estado: RecipientStatus
  motivo_parada: string
  inscrito_at: string | null
  respondido_at: string | null
  respuesta_texto: string
  ultimo_evento: string | null
  ultimo_evento_at: string | null
}

export interface RecipientsPage {
  count: number
  results: CampaignRecipient[]
}

export interface RecipientsQuery {
  page?: number
  estado?: string
  q?: string
  gestor?: string
}

export interface SimulatedSend {
  telefono: string
  nombre: string
  paso: number
  plantilla: string
  /** Por posición: `{"1": "Ana"}`. */
  variables: Record<string, string>
  due_at: string
}

export interface SimulatedSkip {
  telefono: string
  nombre: string
  paso: number
  motivo: string
}

export interface CampaignSimulation {
  envios: SimulatedSend[]
  omitidos: SimulatedSkip[]
}

export interface CampaignStats {
  destinatarios: number
  enviados: number
  entregados: number
  leidos: number
  respondidos: number
  bajas: number
  bajas_por_revisar: number
  fallidos: number
  parados_gestion: number
  descartados_consentimiento: number
  visitas: number
}

export interface CampaignStatsQuery {
  desde?: string
  hasta?: string
  gestor?: string
}

export type ConsentMode = 'observar' | 'aplicar'

export interface CampaignSettings {
  despacho_activo: boolean
  limite_diario: number
  limite_minuto: number
  phone_number_id_campanas: string
  waba_id: string
  hora_inicio: string
  hora_fin: string
  /** Lunes = 0. */
  dias_laborables: number[]
  dias_no_laborables: string[]
  rgpd_id_whatsapp: number
  rgpd_modos: Record<string, ConsentMode>
  escribir_consentimiento_crm: boolean
}

export interface ConsentSummary {
  dias: number
  /** Por `send_type`, cuántas consultas dieron cada resultado. */
  por_send_type: Record<string, Record<string, number>>
}

export interface FilterOptions {
  provincias: string[]
  municipios: string[]
  poblaciones: string[]
  gestores: Array<{ id: string; nombre: string }>
  tipos_inmueble: string[]
  precio_min: number | null
  precio_max: number | null
  /** Falta si la web no ha respondido: entonces no se ofrece filtrar por zona. */
  zonas?: Array<CampaignZone & { municipio?: string }>
  /** `false` mientras no acaba la carga de gestiones: «días sin gestión» aún no sería cierto. */
  gestiones_cargadas?: boolean
}

function withQuery(path: string, params: object = {}): string {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') query.set(key, String(value))
  }
  const text = query.toString()
  return text ? `${path}?${text}` : path
}

const asJson = (method: string, body?: unknown): RequestInit => ({
  method,
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
})

export interface OptOutReview {
  id: string
  telefono: string
  mensaje: string | null
  conversation_id: string | null
  probabilidad: number | null
  estado: 'pendiente' | 'confirmada' | 'descartada'
  created_at: string
}

class CrmCampaignsServiceClient {
  async list(): Promise<CrmCampaign[]> {
    const data = await drfRequest<CrmCampaign[] | { results: CrmCampaign[] }>(`${BASE}/`)
    return Array.isArray(data) ? data : data.results
  }

  create(data: NewCampaign) {
    return drfRequest<CrmCampaign>(`${BASE}/`, asJson('POST', data))
  }

  get(id: string) {
    return drfRequest<CrmCampaign>(`${BASE}/${id}/`)
  }

  update(id: string, data: Partial<NewCampaign>) {
    return drfRequest<CrmCampaign>(`${BASE}/${id}/`, asJson('PATCH', data))
  }

  replaceSteps(id: string, steps: CampaignStep[]) {
    return drfRequest<CampaignStep[]>(`${BASE}/${id}/steps/`, asJson('PUT', steps))
  }

  previewAudience(id: string) {
    return drfRequest<AudiencePreview>(`${BASE}/${id}/audience/preview/`, asJson('POST', {}))
  }

  getAudienceConsent(id: string) {
    return drfRequest<AudienceConsent>(`${BASE}/${id}/audience/consent/`)
  }

  importCsv(id: string, file: File) {
    const body = new FormData()
    body.append('file', file)
    return drfRequest<CsvReport>(`${BASE}/${id}/audience/csv/`, { method: 'POST', body })
  }

  listRecipients(id: string, params: RecipientsQuery = {}) {
    return drfRequest<RecipientsPage>(withQuery(`${BASE}/${id}/recipients/`, params))
  }

  updateRecipients(id: string, ids: string[], included: boolean) {
    return drfRequest<unknown>(`${BASE}/${id}/recipients/`, asJson('PATCH', { ids, incluido: included }))
  }

  simulate(id: string, startDate?: string) {
    return drfRequest<CampaignSimulation>(
      `${BASE}/${id}/simulate/`,
      asJson('POST', startDate ? { inicio: startDate } : {}),
    )
  }

  testSend(id: string, step: number, phone: string) {
    return drfRequest<unknown>(`${BASE}/${id}/test-send/`, asJson('POST', { paso: step, telefono: phone }))
  }

  launch(id: string, startDate?: string) {
    return drfRequest<CrmCampaign>(
      `${BASE}/${id}/launch/`,
      asJson('POST', startDate ? { inicio: startDate } : {}),
    )
  }

  pause(id: string) {
    return drfRequest<CrmCampaign>(`${BASE}/${id}/pause/`, asJson('POST', {}))
  }

  resume(id: string) {
    return drfRequest<CrmCampaign>(`${BASE}/${id}/resume/`, asJson('POST', {}))
  }

  remove(id: string) {
    return drfRequest<void>(`${BASE}/${id}/`, { method: 'DELETE' })
  }

  listOptOutReviews() {
    return drfRequest<OptOutReview[]>(`${BASE}/opt-out-reviews/`)
  }

  decideOptOutReview(id: string, accion: 'confirmar' | 'descartar') {
    return drfRequest<OptOutReview>(`${BASE}/opt-out-reviews/${id}/`, asJson('POST', { accion }))
  }

  archive(id: string) {
    return drfRequest<CrmCampaign>(`${BASE}/${id}/archive/`, asJson('POST', {}))
  }

  stats(id: string, params: CampaignStatsQuery = {}) {
    return drfRequest<CampaignStats>(withQuery(`${BASE}/${id}/stats/`, params))
  }

  getSettings() {
    return drfRequest<CampaignSettings>(`${BASE}/settings/`)
  }

  updateSettings(data: CampaignSettings) {
    return drfRequest<CampaignSettings>(`${BASE}/settings/`, asJson('PUT', data))
  }

  consentSummary(days = 30) {
    return drfRequest<ConsentSummary>(withQuery(`${BASE}/consent/summary/`, { dias: days }))
  }

  filterOptions() {
    return drfRequest<FilterOptions>(`${BASE}/filter-options/`)
  }
}

export const crmCampaignsService = new CrmCampaignsServiceClient()
